import React, { useCallback, useEffect, useRef, useState } from "react";
import "react-complex-tree/lib/style-modern.css";
import { useLocation, useMatch, useNavigate, useParams } from "react-router-dom";

import styled, { css } from "styled-components";

import Alert from "components/atoms/alert/Alert";
import useToast from "components/atoms/toast/useToast";

import SpacePaymentNewModal from "components/page/modal/SpacePaymentNewModal";
import SpaceBar from "components/templates/SpaceBar";
import SpaceMenuBar, {
    SIDE_WIDTH_AUTOFOLD,
    SIDE_WIDTH_BASE,
    SIDE_WIDTH_MAX,
    SIDE_WIDTH_MIN,
} from "components/templates/SpaceMenuBar";
import AlertBanner from "components/templates/banner/AlertBanner";
import PageHeader from "components/templates/header/PageHeader";

import { useTagBulkDelete } from "hooks/queries/useReference";
import { useSpace, useUser } from "hooks/queries/useUser";

import Constants from "utils/constants";
import { useDevice } from "utils/device";
import { isActiveSpace, isAdminUser, isViewerUser } from "utils/user";

const SIDEBAR_DATA_KEY = "sidebarData";

const getInitialActiveTab = () => {
    if (typeof window === "undefined") {
        return "Folders";
    }

    return sessionStorage.getItem("activeTab") || "Folders";
};

const getSidebarData = () => {
    if (typeof window === "undefined") {
        return {
            isFold: false,
            width: SIDE_WIDTH_BASE,
        };
    }

    const sidebarData = JSON.parse(localStorage.getItem(SIDEBAR_DATA_KEY));
    const isFold = sidebarData?.f || false;
    let width = sidebarData?.w || SIDE_WIDTH_BASE;

    if (isFold) {
        width = SIDE_WIDTH_MIN;
    } else {
        width = Math.max(width, SIDE_WIDTH_BASE);
    }

    return {
        isFold,
        width,
    };
};

const Container = styled.div`
    width: 100%;
    height: 100%;
    overflow: auto;
`;

const BodyContainer = styled.div`
    position: relative;
    display: flex;
    margin: ${Constants.MOBILE_HEADER_HEIGHT}px 0 0 0;
    width: 100%;
    height: calc(100vh - ${Constants.MOBILE_HEADER_HEIGHT}px);
    background-color: var(--color-White);
    overflow: hidden;

    ${(props) =>
        props.$device.isTabletOrPC &&
        css`
            margin: ${Constants.TABLET_OR_PC_HEADER_HEIGHT}px 0 0 0;
            height: calc(100vh - ${Constants.TABLET_OR_PC_HEADER_HEIGHT}px);
        `};
`;

//region: 본문 스타일
const MainContentsContainer = styled.div`
    position: absolute;
    top: 0;
    right: 0;
`;

const MainContentsWrap = styled.div`
    position: relative;
    width: 100%;
    height: 100%;
    overflow: auto;
`;

const MainContentsDimLayer = styled.div`
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 100%;

    visibility: ${({ $isFold }) => ($isFold ? "hidden" : "visible")};
    opacity: ${({ $isFold }) => ($isFold ? "0" : "1")};
    transition: ${({ $isFold }) => ($isFold ? "none" : "all 200ms ease-in")};
    z-index: ${({ $isFold }) => ($isFold ? "-100" : "5")};

    ${Constants.DIMED_OPTION};
    ${(props) =>
        props.$device.isPC &&
        css`
            display: none;
        `};
`;

const MainContents = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    ${(props) => (props.$minWidth ? `min-width: ${props.$maxWidth};` : "min-width: 671px;")}
    ${(props) => (props.$maxWidth ? `max-width: ${props.$maxWidth};` : "")}
    background-color: var(--color-White);
`;

//region: 컴포넌트 정의 start
function LoginUserLayout(props) {
    const { children, searchParams, minWidth, maxWidth } = props;
    const device = useDevice();
    const wBarRef = useRef(null);
    const initialSidebarState = getSidebarData();

    const [isFold, setIsFold] = useState(initialSidebarState.isFold);

    const [wBarSize, setWBarSize] = useState(initialSidebarState.width);
    const [wBarResize, setWBarResize] = useState(undefined);
    const [wBarHover, setWBarHover] = useState(false);

    const [showTagAddModal, setShowTagAddModal] = useState(false);
    const [showFolderAddModal, setShowFolderAddModal] = useState({
        type: false,
    });

    const { space_uuid } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    const tagDelete = useTagBulkDelete();
    const { setToast } = useToast();

    const user = useUser().data;

    const spaceQuery = useSpace();
    const spaces = spaceQuery.data;

    const saveSidebarSize = useCallback(() => {
        let oldData = JSON.parse(localStorage.getItem(SIDEBAR_DATA_KEY));

        let w_px = wBarRef.current.style.width;
        let w = typeof w_px === "string" ? parseInt(w_px.substring(0, w_px.length - 2)) : w_px;

        let sidebarData = JSON.stringify({
            w: w,
            f: oldData?.f || false,
        });

        localStorage.setItem(SIDEBAR_DATA_KEY, sidebarData);
    }, []);

    const saveSidebarFold = useCallback((fold) => {
        let oldData = JSON.parse(localStorage.getItem(SIDEBAR_DATA_KEY));

        let sidebarData = JSON.stringify({
            w: oldData?.w || SIDE_WIDTH_BASE,
            f: fold,
        });

        localStorage.setItem(SIDEBAR_DATA_KEY, sidebarData);
    }, []);

    const loadSidebarData = useCallback(() => {
        const sidebarData = getSidebarData();
        setIsFold(sidebarData.isFold);
        setWBarSize(sidebarData.width);
    }, []);

    const handleWindowResize = useCallback(() => {
        if (window.innerWidth < Constants.PC_WIDTH) {
            saveSidebarFold(true);
            setIsFold(true);
            loadSidebarData();
        } else {
            saveSidebarFold(false);
            setIsFold(false);
            loadSidebarData();
        }
    }, [loadSidebarData, saveSidebarFold]);

    // 사이드메뉴 리사이즈
    useEffect(() => {
        // 브라우저 창 크기가 변했을 경우 처리
        window.addEventListener("resize", handleWindowResize);
        return () => {
            window.removeEventListener("resize", handleWindowResize);
        };
    }, [handleWindowResize]);

    useEffect(() => {
        // 리사이즈 후 특정 너비보다 작으면 자동으로 fold 상태로 전환
        if (wBarResize === false) {
            if (wBarSize < SIDE_WIDTH_AUTOFOLD) {
                setIsFold(true);
                setWBarSize(SIDE_WIDTH_MIN);
                saveSidebarSize();
                saveSidebarFold(true);
            }
        }
    }, [saveSidebarFold, saveSidebarSize, wBarResize, wBarSize]);

    const startWBarResizing = () => {
        if (!isFold) {
            setWBarResize(true);
            window.addEventListener("mousemove", applyWBarResizing);
            window.addEventListener("mouseup", stopWBarResizing);
        }
    };

    const applyWBarResizing = (e) => {
        if (wBarRef.current) {
            const newWidth = e.clientX - wBarRef.current.getBoundingClientRect().left;
            const clampedWidth = Math.min(Math.max(newWidth, 1), SIDE_WIDTH_MAX);
            setWBarSize(clampedWidth);
        }
    };

    const stopWBarResizing = () => {
        if (!isFold) {
            setWBarResize(false);
            window.removeEventListener("mousemove", applyWBarResizing);
            window.removeEventListener("mouseup", stopWBarResizing);

            saveSidebarSize();
        }
    };

    const toggleSidebar = (e) => {
        e.stopPropagation();

        if (isFold) {
            setIsFold(false);
            saveSidebarFold(false);
            loadSidebarData();
        } else {
            setIsFold(true);
            saveSidebarFold(true);
            loadSidebarData();
        }
    };

    // Tab 선택 상태 관리
    const [activeTab, setActiveTab] = useState(getInitialActiveTab);

    const handleTabClick = (tab) => {
        setActiveTab(tab);
        sessionStorage.setItem("activeTab", tab);
    };

    const formatBytes = (bytes, decimals = 2) => {
        if (bytes === 0) return "0 Bytes";
        const k = 1024;
        const dm = decimals < 0 ? 0 : decimals;
        const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
    };

    const [showPaidPlanSubscribeModal, setShowPaidPlanSubscribeModal] = useState();
    const isPathSearch = useMatch("/:group_uuid/search") !== null;
    const isPathReport = useMatch("/:group_uuid/report") !== null;
    const isPathNote = useMatch("/:group_uuid/note") !== null;
    const isPathManuscriptRoot = useMatch("/:group_uuid/manuscript") !== null;
    const isPathManuscriptNested = useMatch("/:group_uuid/manuscript/*") !== null;
    const isPathManuscript = isPathManuscriptRoot || isPathManuscriptNested;
    const isPathReference = useMatch("/:group_uuid/reference") !== null;
    const isPathAccount = useMatch("/account/*") !== null;
    const isPathSettings = useMatch("/settings/*") !== null;
    const isPathPlan = useMatch("/plan/*") !== null;
    const isSpacePath = !(isPathAccount || isPathSettings || isPathPlan);

    const volume = user?.current_space?.volume_info;
    const isVolumeWarn = ((volume?.used / volume?.max) * 100).toFixed(0) >= 90;

    // 초대 중인 스페이스 케이스 처리
    useEffect(() => {
        if (!space_uuid || !spaces) return;
        const currentSpace = spaces.find((s) => s.uuid === space_uuid);
        if (
            currentSpace &&
            currentSpace.my_member_info.invitation_status === Constants.SPACE_MEMBER_INVITATION_STATUS_PENDING
        ) {
            if (location.pathname !== `/${space_uuid}/management`) {
                Alert("warn", "스페이스 초대 대기 중", "그룹에 가입한 후 이용해주세요.");
                navigate(`/${space_uuid}/management`);
            }
        }
    }, [spaces, location.pathname, navigate, space_uuid]);

    const primaryMenuActiveMap = {
        search: isPathSearch,
        report: isPathReport,
        note: isPathNote,
        manuscript: isPathManuscript,
    };

    //region: 페이지 레이아웃 마크업 시작
    return (
        <Container>
            <PageHeader />
            <BodyContainer $device={device}>
                <SpaceBar />
                {isSpacePath ? (
                    <SpaceMenuBar
                        device={device}
                        wBarRef={wBarRef}
                        isFold={isFold}
                        wBarSize={wBarSize}
                        wBarResize={wBarResize}
                        wBarHover={wBarHover}
                        startWBarResizing={startWBarResizing}
                        setWBarHover={setWBarHover}
                        toggleSidebar={toggleSidebar}
                        spaces={spaces}
                        space_uuid={space_uuid}
                        navigate={navigate}
                        isPathReference={isPathReference}
                        activeTab={activeTab}
                        handleTabClick={handleTabClick}
                        location={location}
                        setToast={setToast}
                        tagDelete={tagDelete}
                        searchParams={searchParams}
                        showTagAddModal={showTagAddModal}
                        setShowTagAddModal={setShowTagAddModal}
                        showFolderAddModal={showFolderAddModal}
                        setShowFolderAddModal={setShowFolderAddModal}
                        user={user}
                        setShowPaidPlanSubscribeModal={setShowPaidPlanSubscribeModal}
                        volume={volume}
                        isVolumeWarn={isVolumeWarn}
                        formatBytes={formatBytes}
                        primaryMenuActiveMap={primaryMenuActiveMap}
                    />
                ) : null}
                <MainContentsContainer
                    style={
                        isSpacePath
                            ? {
                                  width: `calc(100% - ${wBarSize}px - ${Constants.SPACE_BAR_WIDTH}px)`,
                              }
                            : { width: `calc(100% - ${Constants.SPACE_BAR_WIDTH}px)` }
                    }
                >
                    {isSpacePath && (
                        <>
                            {(volume?.used / volume?.max) * 100 >= 100 && (
                                <AlertBanner
                                    alertMessage="저장 공간을 모두 사용했습니다. 구독중인 요금제를 확인해주세요."
                                    showPlan={true}
                                    onAction={() => {
                                        navigate("/plan");
                                    }}
                                />
                            )}
                            {user?.current_space && !isActiveSpace(user.current_space) && (
                                <AlertBanner
                                    alertMessage="이 스페이스는 요금제가 만료되어 읽기만 가능합니다."
                                    showReSub={isAdminUser(user)}
                                    onAction={() => {
                                        navigate(`/${space_uuid}/management?tab=payment`);
                                    }}
                                />
                            )}
                            {user?.current_space && isViewerUser(user) && (
                                <AlertBanner alertMessage="이 스페이스에 뷰어로 참여하고 있습니다. 읽기만 가능합니다." />
                            )}
                        </>
                    )}
                    {!device.isTabletOrPC && (
                        <AlertBanner alertMessage="원활한 이용을 위해 데스크탑 환경을 권장드립니다." />
                    )}
                    <MainContentsWrap>
                        <MainContentsDimLayer
                            $device={device}
                            $isFold={isFold}
                            onClick={(e) => {
                                setIsFold(true);
                                setWBarSize(SIDE_WIDTH_MIN);
                            }}
                        />
                        <MainContents $minWidth={minWidth} $maxWidth={maxWidth}>
                            {children}
                        </MainContents>
                    </MainContentsWrap>
                </MainContentsContainer>
            </BodyContainer>
            {showPaidPlanSubscribeModal && (
                <SpacePaymentNewModal
                    user={user}
                    exitModal={(e) => setShowPaidPlanSubscribeModal(!showPaidPlanSubscribeModal)}
                />
            )}
        </Container>
    );
}

export default LoginUserLayout;
